import { useCallback, useMemo, useState } from "react";

import {
  Badge,
  Button,
  Table,
  TableColumnDefinition,
  TableQuery,
  FontIcon,
  TableRowDefinition,
  Typography,
} from "@ezmid/components-library/";
import { faDatabase, faList } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

import {
  useGetBackOfficeListCoursesQuery,
  useDeleteCourseByIdMutation,
} from "../../../api";
import { DeleteConfirmationModal } from "../../../components/modals/DeleteConfirmationModal";
import { useFormatters } from "../../../hooks";
import { makeRoute } from "../../../hooks/route";
import { useTableQuery } from "../../../hooks/useTableQuery";
import { truncateWithEllipsis } from "../../../utils/stringUtils";
import { AllRoutes } from "../../route/Route";

const localStorageKey = "course.page";

type TableData = {
  name: string;
  lecturer: string;
  createdAt: string;
  numberOfLessons: number;
  status?: string;
};

const STATUS_BADGE_MAP: Record<
  string,
  { badgeStatus: "success" | "warning" | "danger"; statusKey: string }
> = {
  "cs-active": {
    badgeStatus: "success",
    statusKey: "form.status.active",
  },
  "cs-preview": {
    badgeStatus: "warning",
    statusKey: "form.status.preview",
  },
  "cs-draft": {
    badgeStatus: "danger",
    statusKey: "form.status.draft",
  },
};

const mapStatusToBadge = (
  status: string | undefined,
  t: (key: string) => string
): { badgeStatus: "success" | "warning" | "danger"; statusLabel: string } => {
  const mapping =
    STATUS_BADGE_MAP[status || "cs-draft"] || STATUS_BADGE_MAP["cs-draft"];
  return {
    badgeStatus: mapping.badgeStatus,
    statusLabel: t(mapping.statusKey),
  };
};

export function CoursesListPageContent<T extends object>() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { formatDate } = useFormatters();

  const langCode = i18n.language.split("-")[0];

  const { query, updateQuery } = useTableQuery(
    localStorageKey,
    {
      pageSize: 10,
      pageIndex: 1,
      keyword: "",
      sortBy: { key: "name", order: "desc" },
    },
    AllRoutes.COURSES_LIST
  );

  const { currentData, isFetching, refetch } = useGetBackOfficeListCoursesQuery(
    {
      page: query?.pageIndex,
      size: query?.pageSize,
      keyword: query?.keyword,
      sort: [`${query.sortBy?.key}:${query.sortBy?.order}`],
    },
    { refetchOnMountOrArgChange: true }
  );

  const [deleteCourse, { isLoading: isDeleting }] =
    useDeleteCourseByIdMutation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const handleDeleteClick = useCallback(
    (courseId: string, courseName: string) => {
      setCourseToDelete({ id: courseId, name: courseName });
      setIsDeleteModalOpen(true);
    },
    []
  );

  const handleConfirmDelete = async () => {
    if (!courseToDelete) return;

    try {
      await deleteCourse({ id: courseToDelete.id }).unwrap();
      toast.success(t("app.course.delete.success"));
      setIsDeleteModalOpen(false);
      setCourseToDelete(null);
      refetch();
    } catch (error) {
      toast.error(t("app.course.delete.failed"));
    }
  };

  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false);
      setCourseToDelete(null);
    }
  };

  const columns: Array<TableColumnDefinition<TableData>> = useMemo(
    () => [
      {
        Header: t("table.header.name"),
        id: "name",
        accessor: "name",
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Typography variant="body1">
            {truncateWithEllipsis(row?.original.name, 30)}
          </Typography>
        ),
      },
      {
        Header: t("table.header.lecturer"),
        id: "lecturer",
        accessor: "lecturer",
        width: "350px",
      },
      {
        Header: t("table.header.type.lessonsNumber"),
        Cell: ({ row }: TableColumnDefinition<T>) => (
          <Badge
            variant="pill"
            status="break"
            leftIcon={
              <FontIcon
                marginTop={3}
                customIcon={<FontAwesomeIcon size="lg" icon={faDatabase} />}
              />
            }
          >
            {row?.original.numberOfLessons ?? ""}
          </Badge>
        ),
        id: "numberOfLessons",
        accessor: "numberOfLessons",
        width: "300px",
      },
      {
        Header: t("table.header.status"),
        Cell: ({ row }: TableColumnDefinition<T>) => {
          const { badgeStatus, statusLabel } = mapStatusToBadge(
            row?.original.status,
            t
          );

          return (
            <Badge variant="pill" status={badgeStatus}>
              {statusLabel}
            </Badge>
          );
        },
        id: "status",
        accessor: "status",
        width: "200px",
      },

      {
        Header: t("table.header.createdAt"),
        Cell: ({ row }: TableColumnDefinition<T>) => (
          <span>{formatDate(row?.original.createdAt)}</span>
        ),
        id: "createdAt",
        accessor: "createdAt",
        width: "250px",
      },
      {
        Header: "",
        Cell: ({ row }: TableRowDefinition<T>) => (
          <div className="flex justify-end pr-2">
            <Button
              variant="danger"
              size="sm"
              style={{ borderRadius: "0.2rem" }}
              onClick={() => {
                handleDeleteClick(row?.original.id, row?.original.name);
              }}
            >
              {t("button.delete")}
            </Button>
          </div>
        ),
        id: "actions",
        accessor: "id",
        width: "150px",
      },
    ],
    [handleDeleteClick, t, formatDate]
  );

  const memoizedData: Array<TableData> = useMemo(
    () =>
      currentData?.items?.map((course) => {
        const translatedName = course?.translations?.find(
          (translation) => translation?.language === langCode
        )?.name;
        return {
          name: translatedName || "",
          lecturer: course?.lecturer?.fullName || "",
          numberOfLessons: course?.lessons?.length || 0,
          createdAt: course?.createdAt || "",
          status: course?.status || "cs-draft",
          id: course?.id || "",
        };
      }) || [],
    [currentData, langCode]
  );

  const onFetchData = ({
    pageIndex,
    pageSize,
    keyword: queryKeyword,
    sortBy,
  }: TableQuery) => {
    updateQuery({
      pageIndex,
      pageSize,
      keyword: queryKeyword || "",
      sortBy,
    });
  };

  return (
    <div className="w-full min-w-full p-2">
      <div>
        <Table
          headerIcon={
            <FontIcon
              marginTop={4}
              customIcon={<FontAwesomeIcon icon={faList} />}
            />
          }
          headerText={t("card.header.list")}
          tableData={memoizedData}
          tableColumns={columns}
          showPagination
          shouldAddEmptyRowsOnLastPage={currentData?.last}
          showToolbar
          onRowSelect={(row) =>
            navigate(
              makeRoute(AllRoutes.COURSE_DETAIL, { id: row.original?.id })
            )
          }
          isLoading={isFetching}
          totalCountOfRecords={currentData?.count || 0}
          onFetchData={onFetchData}
          query={{
            pageIndex: query.pageIndex,
            pageSize: query.pageSize,
            keyword: query.keyword,
            sortBy: query.sortBy,
          }}
        />
      </div>
      <DeleteConfirmationModal
        isModalOpen={isDeleteModalOpen}
        onModalClose={handleCloseDeleteModal}
        onConfirmDelete={handleConfirmDelete}
        itemName={courseToDelete?.name || ""}
        isDeleting={isDeleting}
        titleKey="modal.deleteCourse.heading"
        confirmMessageKey="app.course.delete.confirm"
      />
    </div>
  );
}
